import * as emoji from 'node-emoji';
import * as linkify from 'linkifyjs';
import sanitizeHtml from 'sanitize-html';

export type MdToHTMLOptions = {
  truncate?: number;
  linkClassName?: string;
};

const placeholderPrefix = `__MD_LINK_${Math.random().toString(36).slice(2)}__`;
const mdPlaceholder = (index: number) => `${placeholderPrefix}${index}__`;

const escapeHtml = (str: string): string =>
  str.replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char] || char),
  );

const maybeTruncate = (text: string, limit?: number) => {
  if (!limit) return text;

  const chars = Array.from(text);
  if (chars.length <= limit) return text;

  return `${chars.slice(0, limit).join('')}...`;
};

const isEscaped = (value: string, index: number) => {
  let backslashes = 0;
  let i = index - 1;
  while (i >= 0 && value[i] === '\\') {
    backslashes += 1;
    i -= 1;
  }
  return backslashes % 2 === 1;
};

const unescapeMarkdown = (value: string) =>
  value.replace(/\\([\\[\]()])/g, '$1');

const buildAnchor = (
  href: string,
  text: string,
  options?: MdToHTMLOptions,
): string => {
  const allowedSchemes = ['http', 'https', 'mailto', 'tel'];
  const hasScheme = href.includes(':');
  const scheme = hasScheme
    ? href.slice(0, href.indexOf(':')).toLowerCase()
    : '';
  const normalizedHref = (() => {
    if (allowedSchemes.includes(scheme)) return href;
    if (hasScheme) return null;
    if (href.startsWith('//')) return `https:${href}`;
    return `https://${href}`;
  })();
  const className = options?.linkClassName
    ? ` class="${escapeHtml(options.linkClassName)}"`
    : '';
  const hrefAttr = normalizedHref
    ? ` href="${escapeHtml(normalizedHref)}"`
    : '';
  const display = maybeTruncate(escapeHtml(text), options?.truncate);

  return `<a${hrefAttr} target="_blank" rel="noopener noreferrer"${className}>${display}</a>`;
};

const findMatchingBracket = (text: string, startIndex: number) => {
  let depth = 1;
  for (let i = startIndex + 1; i < text.length; i += 1) {
    const char = text[i];
    if (char === '[' && !isEscaped(text, i)) depth += 1;
    if (char === ']' && !isEscaped(text, i)) depth -= 1;
    if (depth === 0) return i;
  }
  return -1;
};

const findMatchingParen = (text: string, startIndex: number) => {
  let depth = 1;
  for (let i = startIndex + 1; i < text.length; i += 1) {
    const char = text[i];
    if (char === '(' && !isEscaped(text, i)) depth += 1;
    if (char === ')' && !isEscaped(text, i)) depth -= 1;
    if (depth === 0) return i;
  }
  return -1;
};

const replaceMarkdownLinks = (
  text: string,
  options?: MdToHTMLOptions,
): { textWithPlaceholders: string; anchors: string[] } => {
  const anchors: string[] = [];
  let result = '';
  let cursor = 0;

  while (cursor < text.length) {
    const openIndex = text.indexOf('[', cursor);
    if (openIndex === -1) {
      result += text.slice(cursor);
      break;
    }

    if (isEscaped(text, openIndex)) {
      result += text.slice(cursor, openIndex + 1);
      cursor = openIndex + 1;
      continue;
    }

    const closeIndex = findMatchingBracket(text, openIndex);
    if (closeIndex === -1) {
      result += text.slice(cursor);
      break;
    }

    const openParenIndex = closeIndex + 1;
    if (text[openParenIndex] !== '(' || isEscaped(text, openParenIndex)) {
      result += text.slice(cursor, openParenIndex);
      cursor = openParenIndex;
      continue;
    }

    const closeParenIndex = findMatchingParen(text, openParenIndex);
    if (closeParenIndex === -1) {
      result += text.slice(cursor);
      break;
    }

    const label = unescapeMarkdown(text.slice(openIndex + 1, closeIndex));
    const href = unescapeMarkdown(
      text.slice(openParenIndex + 1, closeParenIndex),
    );

    const anchor = buildAnchor(href, label, options);
    anchors.push(anchor);

    result += text.slice(cursor, openIndex);
    result += mdPlaceholder(anchors.length - 1);
    cursor = closeParenIndex + 1;
  }

  return {
    textWithPlaceholders: result,
    anchors,
  };
};

const linkifyText = (text: string, options?: MdToHTMLOptions): string => {
  const matches = linkify.find(text);

  if (!matches.length) {
    return text;
  }

  let result = '';
  let lastIndex = 0;

  matches.forEach((match) => {
    result += text.slice(lastIndex, match.start);
    result += buildAnchor(match.href, match.value, options);
    lastIndex = match.end;
  });

  result += text.slice(lastIndex);

  return result;
};

export const mdToHTML = (text: string, options?: MdToHTMLOptions): string => {
  const { textWithPlaceholders, anchors: markdownAnchors } =
    replaceMarkdownLinks(text, options);

  const withAutoLinks = linkifyText(textWithPlaceholders, options);

  const placeholderRegex = new RegExp(`${placeholderPrefix}(\\d+)__`, 'g');
  const restoredMarkdownLinks = withAutoLinks.replace(
    placeholderRegex,
    (_match, index) => markdownAnchors[Number(index)] ?? '',
  );

  const sanitized = sanitizeHtml(restoredMarkdownLinks, {
    allowedTags: ['a'],
    allowedAttributes: {
      a: ['href', 'name', 'target', 'rel', 'class'],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesAppliedToAttributes: ['href'],
    disallowedTagsMode: 'escape',
  });

  return emoji.emojify(sanitized);
};
